'use client';
import React from 'react';
import Theme from './types/theme';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { getDefaultTheme, getThemeFromName, ThemeNames } from './themes';
import ColorBlindModes from './types/theme/colorBlindModes';

export interface Props {
  debugAfterHydration?: boolean;
  isNext?: boolean;
  prefix?: string;
  theme: ThemeNames | Theme;
  colorBlind?: ColorBlindModes;
}

const shouldUpdate = (
  prevProps: Readonly<React.PropsWithChildren<Props>>,
  nextProps: Readonly<React.PropsWithChildren<Props>>,
): boolean => {
  return !(prevProps.theme !== nextProps.theme || prevProps.isNext || prevProps.children !== nextProps.children);
};

export const getTheme = (themeObject: ThemeNames | Theme, colorBlindMode: ColorBlindModes = 'none'): Theme => {
  if (typeof themeObject == 'string') {
    const currentTheme = getThemeFromName(themeObject);
    currentTheme.colors.blindMode = colorBlindMode;
    const lightYellow = '#f4da01';
    const darkYellow = '#806a00';
    const yellowColor = themeObject === 'light' ? darkYellow : lightYellow;
    switch (colorBlindMode) {
      case 'red':
        currentTheme.colors.statistic.negative = yellowColor;
        currentTheme.colors.statistic.positive = '#30bfa3';
        break;
      case 'green':
        currentTheme.colors.statistic.negative = '#e5594e';
        currentTheme.colors.statistic.positive = yellowColor;
        break;
      case 'none':
        currentTheme.colors.statistic.negative = '#e5594e';
        currentTheme.colors.statistic.positive = '#30bfa3';
        break;
    }
    return currentTheme;
  } else if (typeof themeObject == 'object') {
    return themeObject;
  } else {
    console.error('theme argument invalid reverting to the dark default theme');
    return getThemeFromName(getDefaultTheme());
  }
};

const ThemeProvider: React.FC<React.PropsWithChildren<Props>> = React.memo(props => {
  const { children, colorBlind, theme } = props;
  const processedTheme = React.useMemo(() => getTheme(theme, colorBlind), [theme, colorBlind]);

  return <SCThemeProvider theme={processedTheme}>{children}</SCThemeProvider>;
}, shouldUpdate);

export default ThemeProvider;
